'use client'

// The one place allowed to import sonner's toast: this wrapper turns raw
// version-skew errors into the friendly refresh prompt for every call site.
// eslint-disable-next-line no-restricted-imports
import { toast as sonnerToast } from 'sonner'
import {
  isVersionSkewError,
  VERSION_SKEW_REFRESH_LABEL,
  VERSION_SKEW_TOAST_DESCRIPTION,
  VERSION_SKEW_TOAST_TITLE,
} from '@/lib/version-skew'

export const VERSION_SKEW_TOAST_ID = 'version-skew'

/**
 * Persistent (until dismissed) so the user can finish copying any typed input
 * before refreshing; the fixed id collapses repeated failures into one toast.
 */
export function showVersionSkewToast(): string | number {
  return sonnerToast.error(VERSION_SKEW_TOAST_TITLE, {
    id: VERSION_SKEW_TOAST_ID,
    duration: Infinity,
    description: VERSION_SKEW_TOAST_DESCRIPTION,
    action: {
      label: VERSION_SKEW_REFRESH_LABEL,
      onClick: () => {
        window.location.reload()
      },
    },
  })
}

/** Shows the refresh prompt and returns true when the error is version skew. */
export function handleVersionSkew(error: unknown): boolean {
  if (!isVersionSkewError(error)) return false
  showVersionSkewToast()
  return true
}

const error: typeof sonnerToast.error = (message, options) => {
  if (typeof message === 'string' && handleVersionSkew(message)) {
    return VERSION_SKEW_TOAST_ID
  }
  return sonnerToast.error(message, options)
}

/**
 * Drop-in replacement for sonner's `toast`. Catch blocks all over the app do
 * `toast.error(err.message)`; routing that through here means a stale tab
 * shows the refresh prompt instead of Next's raw error string.
 */
export const toast: typeof sonnerToast = Object.assign(
  ((...args: Parameters<typeof sonnerToast>) => sonnerToast(...args)) as typeof sonnerToast,
  sonnerToast,
  { error }
)
